// Host: subscribes to bus and renders stacks of <ToastItem />.
// No context; can be mounted once near app root.

import * as React from "react";
import { onToast } from "./bus";
import { getToastDefaults } from "./api";
import type { ToastEvent, ToastRecord, ToastPosition } from "./types";
import { ToastItem } from "@/ui/feedback/toast/ToastItem";

export type ToasterHostProps = {
  /** z-index for viewports */
  zIndex?: number;
  /** Override default position for new toasts (fallback) */
  defaultPosition?: ToastPosition;
  /** Maximum number of items per stack (0 => unlimited) */
  maxPerStack?: number;
};

export const ToasterHost: React.FC<ToasterHostProps> = ({
  zIndex = 9999,
  defaultPosition,
  maxPerStack,
}) => {
  const [items, setItems] = React.useState<ToastRecord[]>([]);

  // Handle bus events
  React.useEffect(() => {
    onToast((ev: ToastEvent) => {
      if (ev.type === "ADD") {
        const { id, content, opts } = ev.payload;
        setItems((prev) => {
          const exists = prev.some((t) => t.id === id);
          const defs = getToastDefaults();
          const rec: ToastRecord = {
            id,
            variant: opts.variant ?? "info",
            icon: opts.icon,
            faLeadingIcon: opts.faLeadingIcon,
            text: opts.text,
            title: opts.title,
            dismissible: opts.dismissible ?? defs.dismissible,
            position: opts.position ?? defaultPosition ?? defs.position,
            autoCloseMs: opts.autoCloseMs ?? defs.duration,
            showProgress: opts.showProgress ?? defs.showProgress,
            pauseOnHover: opts.pauseOnHover ?? defs.pauseOnHover,
            className: opts.className ?? "",
            content: opts.content,
          };

          const next = exists ? prev.map((t) => (t.id === id ? rec : t)) : [...prev, rec];

          const limit = maxPerStack ?? defs.maxPerStack;
          if (limit > 0) {
            const grouped = groupByPosition(next);
            const trimmed: ToastRecord[] = [];
            (Object.keys(grouped) as ToastPosition[]).forEach((pos) => {
              const arr = grouped[pos]!;
              if (arr.length > limit) {
                // keep newest N
                trimmed.push(...arr.slice(arr.length - limit));
              } else {
                trimmed.push(...arr);
              }
            });
            // Keep overall chronological order (by initial insertion index)
            // Since we don't store timestamps, preserve array order by position concatenation order
            return trimmed;
          }

          return next;
        });
      }

      if (ev.type === "DISMISS") {
        setItems((prev) => prev.filter((t) => t.id !== ev.payload.id));
      }

      if (ev.type === "CLEAR") {
        setItems([]);
      }
    });
  }, [defaultPosition, maxPerStack]);

  const grouped = React.useMemo(() => groupByPosition(items), [items]);

  return (
    <>
      {(["top-right", "top-left", "bottom-right", "bottom-left"] as ToastPosition[]).map((pos) => {
        const list = grouped[pos];
        if (!list.length) return null;
        return (
          <Viewport key={pos} position={pos} zIndex={zIndex}>
            {list.map((t) => (
              <ToastItem
                key={t.id}
                variant={t.variant}
                icon={t.icon}
                faLeadingIcon={t.faLeadingIcon}
                text={t.text}
                title={t.title}
                dismissible={t.dismissible}
                autoCloseMs={t.autoCloseMs}
                showProgress={t.showProgress}
                pauseOnHover={t.pauseOnHover}
                className={t.className}
                onCloseRequest={() => {
                  // fired when user clicked × or when auto-close triggers
                }}
                onClose={() => {
                  // remove from stack after exit transition
                  setItems((prev) => prev.filter((x) => x.id !== t.id));
                }}
              >
                {/* If you passed content via notify(<b>JSX</b>, ...), it will come as children in the notify() call.
                    We forwarded only options into ToastRecord, not the arbitrary content node.
                    To keep API simple, prefer passing text via opts.text or put JSX into `content` arg when notifying
                    and render it here by storing the content in ToastRecord. If you want that, extend ToastRecord to hold `content?: ReactNode`. */}
                {t.content}
              </ToastItem>
            ))}
          </Viewport>
        );
      })}
    </>
  );
};

function groupByPosition(list: ToastRecord[]) {
  const res: Record<ToastPosition, ToastRecord[]> = {
    "top-right": [],
    "top-left": [],
    "bottom-right": [],
    "bottom-left": [],
  };
  for (const it of list) res[it.position].push(it);
  return res;
}

const Viewport: React.FC<React.PropsWithChildren<{ position: ToastPosition; zIndex: number }>> = ({ position, zIndex, children }) => {
  const isTop = position.includes("top");
  const isRight = position.includes("right");
  const cls = [
    "tw-fixed",
    isTop ? "tw-top-3" : "tw-bottom-3",
    isRight ? "tw-right-3" : "tw-left-3",
    "tw-pointer-events-none",
    "tw-flex",
    "tw-flex-col",
    "tw-gap-2",
    "tw-p-3",
  ].join(" ");


  const style: React.CSSProperties = { zIndex };
  if (isTop && isRight) {
    style.top = "calc(0.75rem + var(--et-task-banner-offset, 0px))";
  }

  return (
    <div className={cls} style={style} aria-live="polite" aria-atomic={false} role="region">
      {/* children must be pointer-events-auto */}
      {children}
    </div>
  );
};
